import { useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContactForm from '@/pages/about/components/ContactForm';
import { films } from '@/mocks/films';
import { applySeo } from '@/utils/seo';

const values = [
  {
    icon: 'ri-book-open-line',
    title: '剧情写清楚',
    desc: '每部作品都配 3 到 4 段分段剧情，按叙事顺序写，不做复制粘贴式的简介。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '资料可核对',
    desc: '上映时间、片长、演职员信息统一整理，发现错误欢迎留言指出。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '只做介绍',
    desc: '本站不提供播放、下载与任何影音文件，所有内容为观影前的参考。',
  },
];

const scopeRows = [
  { label: '电影', value: '院线片、类型片、文艺片与影史经典' },
  { label: '电视剧', value: '热播剧、年代群像、悬疑短剧与治愈系剧集' },
  { label: '地区覆盖', value: '内地、港台、欧美、日韩四个地区的作品' },
  { label: '内容深度', value: '剧情分段介绍、演职员信息、类型标签与相关推荐' },
];

const aboutHeroImage =
  'https://readdy.ai/api/search-image?query=Quiet%20dark%20editorial%20scene%20of%20stacked%20film%20canisters%20and%20open%20notebooks%20on%20a%20deep%20navy%20desk%2C%20soft%20warm%20amber%20desk%20lamp%20glow%2C%20minimal%20atmospheric%20composition%2C%20cinematic%20still&width=1600&height=520&seq=about-hero-01&orientation=landscape';

export default function AboutPage() {
  useEffect(() => {
    applySeo({
      title: '关于我们 - 同学的妈妈影视大全',
      description:
        '了解同学的妈妈影视内容站的定位与内容范围：我们整理电影与电视剧资料、撰写详细剧情介绍，不提供播放与下载服务。',
      keywords: '关于我们,同学的妈妈,影视大全,剧情介绍',
      path: '/about',
    });
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={aboutHeroImage}
            alt="影视资料整理工作台"
            title="关于同学的妈妈影视大全"
            className="h-full w-full object-cover object-top opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40" />
        </div>
        <div className="relative z-10 px-4 py-12 md:px-8 md:py-16 lg:px-12">
          <div className="w-full">
            <p className="font-label text-[11px] uppercase tracking-[0.22em] text-accent-300">About us</p>
            <h1 className="mt-2 font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
              关于「同学的妈妈」
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-[15px]">
              一个偏爱慢慢讲故事的影视内容站。我们相信，选片这件事值得认真一点：先知道它讲了什么，再决定要不要把两个小时交给它。
            </p>
            <div className="mt-6 flex flex-wrap gap-6">
              {[
                { value: films.length, label: '收录影片' },
                { value: 4, label: '覆盖地区' },
                { value: 12, label: '类型标签' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-label text-2xl font-black text-accent-400">{stat.value}</p>
                  <p className="mt-0.5 text-[11px] text-background-300">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 pt-5 md:px-8 lg:px-12">
        <Breadcrumb items={[{ label: '关于我们' }]} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-6 px-4 md:px-8 lg:grid-cols-12 lg:gap-8 lg:px-12">
        <div className="lg:col-span-7">
          <article className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">网站定位</h2>
            <div className="mt-3 space-y-3 text-sm leading-[1.9] text-foreground-700">
              <p>
                「同学的妈妈」是一个面向中国大陆及华语区观众的中文影视内容站。名字取自一种熟悉又有点奇妙的关系：你同桌的妈妈，可能恰好看过你听说过的所有电影，并且总能一句话说清哪部值得看。
              </p>
              <p>
                我们做的事很简单：把电影和电视剧的基础资料整理清楚，把剧情按叙事顺序分段写下来，再告诉你它适合什么样的人、什么样的夜晚。你会在这里看到热播新片，也会看到一些被时间低估的老作品。
              </p>
              <p>
                所有内容均由编辑整理撰写，仅用于观影参考。本站不存储、不提供任何影音文件，不提供播放与下载功能，也不引导跳转至任何第三方播放页面。
              </p>
            </div>

            <h2 className="mt-7 font-heading text-lg font-bold text-foreground-950">内容范围</h2>
            <div className="mt-3 overflow-hidden rounded-md border border-background-200">
              {scopeRows.map((row, index) => (
                <div
                  key={row.label}
                  className={`flex flex-col gap-1 px-3.5 py-3 sm:flex-row sm:items-center sm:gap-4 ${
                    index !== scopeRows.length - 1 ? 'border-b border-background-200' : ''
                  }`}
                >
                  <p className="w-20 shrink-0 text-xs font-medium text-foreground-500">{row.label}</p>
                  <p className="text-xs leading-relaxed text-foreground-800">{row.value}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-7 font-heading text-lg font-bold text-foreground-950">我们坚持的三件事</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {values.map((item) => (
                <div key={item.title} className="rounded-md bg-background-100 p-3.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-accent-600">
                    <i className={`${item.icon} text-base w-4 h-4 flex items-center justify-center`} />
                  </span>
                  <p className="mt-2.5 text-sm font-medium text-foreground-950">{item.title}</p>
                  <p className="mt-1 text-[11px] leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </article>

          <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">版权与免责声明</h2>
            <div className="mt-3 space-y-2.5 text-xs leading-[1.9] text-foreground-600">
              <p>
                <span className="font-medium text-foreground-800">版权声明：</span>
                本站所有文字内容（含剧情介绍、片单整理）版权归本站所有，转载请注明来源「同学的妈妈」。影视作品名称、海报及相关图像版权归原权利方所有，本站仅在介绍性语境中引用。
              </p>
              <p>
                <span className="font-medium text-foreground-800">免责声明：</span>
                本站为内容介绍与演示站点，不提供任何影音文件的存储、上传、播放与下载服务。剧情介绍可能包含剧透，请自行判断是否阅读。如权利方认为内容存在不当引用，可通过下方留言联系我们，我们将在核实后处理。
              </p>
              <p>
                <span className="font-medium text-foreground-800">内容勘误：</span>
                影视资料可能存在时效性差异，欢迎通过留言表单指出错误，我们会尽快核对更新。
              </p>
            </div>
          </section>
        </div>

        <div className="lg:col-span-5">
          <div className="flex flex-col gap-4 lg:sticky lg:top-24">
            <ContactForm />
            <div className="rounded-lg bg-primary-900 p-4 text-background-100 md:p-5">
              <h2 className="flex items-center gap-1.5 font-heading text-sm font-bold text-background-50">
                <i className="ri-mail-line text-accent-300 w-4 h-4 flex items-center justify-center" />
                联系方式
              </h2>
              <ul className="mt-3 space-y-2.5 text-xs">
                <li className="flex items-center justify-between gap-3">
                  <span className="text-primary-200">邮箱</span>
                  <span className="font-label text-background-50">hello@tongxuedemama.site</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-primary-200">合作</span>
                  <span className="font-label text-background-50">press@tongxuedemama.site</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-primary-200">回复时效</span>
                  <span className="font-label text-background-50">三个工作日内</span>
                </li>
              </ul>
              <p className="mt-3 border-t border-primary-800 pt-3 text-[11px] leading-relaxed text-primary-300">
                以上为演示联系信息，仅用于展示页面结构。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}